/* component-buy-buttons.js — <buy-buttons>
   AJAX add-to-cart for the PDP form. Uses KK.cart.add (the ONLY cart caller)
   with sections so the drawer + header bubble re-render, then opens the drawer.
   Inline error region (aria-live) on failure. Syncs disabled/label with the
   selected variant via kk:variant:changed. Sold-out is driven by
   variant.available ONLY.

   buy-path audit 2026-07-31 — this handler owns EVERY submit of the product
   form, including the sticky bar's `<button type="submit" form="…">`, which is a
   form-associated SIBLING living outside <buy-buttons>. A containment query
   (this.querySelector) can never see it, so the in-flight treatment has to be
   collected from the whole document by form association. Errors are surfaced
   next to whichever control the buyer actually pressed (e.submitter), because
   the buy-box error region is guaranteed off-screen when the sticky bar is up. */

/* Buyer-facing German fallback if KK.cart hands us an error without a
   userMessage (contract: err.userMessage is a localized, safe string).
   Resolution order is DOM-rendered locale strings first — never err.message. */
const KK_ADD_ERROR_FALLBACK = 'Der Artikel konnte nicht hinzugefügt werden. Bitte versuche es erneut.';

class BuyButtons extends HTMLElement {
  connectedCallback() {
    this.sectionId = this.dataset.sectionId;
    this.form = this.querySelector('form');
    this.atc = this.querySelector('[data-atc]');
    this.atcText = this.querySelector('[data-atc-text]');
    this.spinner = this.querySelector('[data-atc-spinner]');
    this.errorBox = this.querySelector('[data-buy-error]');
    this.errorText = this.querySelector('[data-buy-error-text]');
    if (!this.form) return;

    // NOTE: `form.id` returns the hidden <input name="id"> element, not the id
    // string (HTMLFormElement named-property override) — always getAttribute.
    this.formId = this.form.getAttribute('id') || '';
    this._pending = false;
    this._available = this.atc ? !this.atc.disabled : true;

    this._onSubmit = (e) => this.onSubmit(e);
    this.form.addEventListener('submit', this._onSubmit);

    // Variant sync.
    if (window.KK && window.KK.pubsub) {
      this._unsub = window.KK.pubsub.subscribe('kk:variant:changed', (p) => {
        if (!p || p.sectionId !== this.sectionId) return;
        this.applyVariant(p.variant);
      });
    }

    // Gift-card recipient toggle.
    const gcrToggle = this.querySelector('[data-gcr-toggle]');
    const gcrFields = this.querySelector('[data-gcr-fields]');
    if (gcrToggle && gcrFields) {
      this._onGcr = () => { gcrFields.hidden = !gcrToggle.checked; };
      gcrToggle.addEventListener('change', this._onGcr);
    }
  }

  disconnectedCallback() {
    if (this.form && this._onSubmit) this.form.removeEventListener('submit', this._onSubmit);
    if (this._unsub) this._unsub();
  }

  /* Every control that submits THIS form: the nested PDP button plus every
     form-associated sibling (the sticky bar ATC). Recomputed per use — the
     sticky bar can be (re-)rendered by a section refresh at any time. */
  _controls() {
    const list = [];
    if (this.atc) list.push(this.atc);
    if (this.formId) {
      const externals = document.querySelectorAll('[type="submit"][form="' + this.formId + '"]');
      for (let i = 0; i < externals.length; i++) {
        if (externals[i] !== this.atc) list.push(externals[i]);
      }
    }
    return list;
  }

  applyVariant(variant) {
    if (!this.atc) return;
    const available = !!(variant && variant.available);
    this._available = available;
    if (this.atcText) {
      this.atcText.textContent = available
        ? this.atc.getAttribute('data-add-text')
        : this.atc.getAttribute('data-soldout-text');
    }
    // Mid-flight a variant change must NOT re-enable a busy control; record the
    // intent so the restore in setLoading(false) lands on the right state.
    if (this._pending) {
      this._controls().forEach((el) => { el.__kkPrevDisabled = !available; });
    } else {
      this.atc.disabled = !available;
    }
    if (available) this.hideError();
  }

  onSubmit(e) {
    if (!window.KK || !window.KK.cart) return; // let native submit happen
    e.preventDefault();
    // Guard symmetry (buy-path audit 2026-07-31): setLoading now sets BOTH
    // `disabled` and aria-busy, and the guard reads BOTH — plus an explicit
    // in-flight flag so a programmatic submit cannot slip past either.
    if (this._pending) return;
    if (this.atc && (this.atc.disabled || this.atc.getAttribute('aria-busy') === 'true')) return;

    // Which control did the buyer actually press? The sticky bar is only visible
    // once the buy box has scrolled off the top, so its failures must not be
    // painted into the (off-screen) buy-box error region alone.
    const submitter = e.submitter || (document.activeElement && document.activeElement.closest
      ? document.activeElement.closest('[type="submit"]')
      : null);

    this.hideError();
    this._pending = true;
    this.setLoading(true);

    // The synchronous prelude (FormData + the section lookup) runs AFTER the busy
    // state is set — a throw here (e.g. a third-party fetch/FormData patch) would
    // otherwise latch every add control forever. Contract rule 4: restore in finally.
    try {
      const fd = new FormData(this.form);
      const item = { id: fd.get('id'), quantity: Number(fd.get('quantity')) || 1 };
      const props = {};
      fd.forEach((val, key) => {
        const m = key.match(/^properties\[(.+)\]$/);
        if (m && val) props[m[1]] = val;
      });
      if (Object.keys(props).length) item.properties = props;
      const sellingPlan = fd.get('selling_plan');
      if (sellingPlan) item.selling_plan = sellingPlan;

      const sections = this._sectionsToRender();

      window.KK.cart.add(item, { sections: sections })
        .then(() => {
          if (window.KK.pubsub) window.KK.pubsub.publish('kk:drawer:open', { id: 'cart' });
        })
        .catch((err) => {
          this.showError(this._userMessage(err), submitter);
        })
        .finally(() => {
          this._pending = false;
          this.setLoading(false);
        });
    } catch (err) {
      this._pending = false;
      this.setLoading(false);
      console.warn('[kk] add-to-cart could not be started', err);
      this.showError(this._userMessage(err), submitter);
    }
  }

  _sectionsToRender() {
    // Ask cart-drawer + header to re-render.
    // buy-path audit 2026-07-31: this used to test getElementById('cart-drawer'),
    // but the element's DOM id is `CartDrawer` (sections/cart-drawer.liquid) — the
    // lookup NEVER matched, the drawer section was never requested, and the drawer
    // opened showing the previous cart until a second round-trip landed. Read the
    // section id the element stamps on itself (as component-free-gift-bar.js does).
    const ids = [];
    const drawer = document.querySelector('cart-drawer[data-section-id]');
    if (drawer) ids.push(drawer.getAttribute('data-section-id'));
    // header bubble section id is 'header' in header.liquid
    ids.push('header');
    return ids.join(',');
  }

  setLoading(on) {
    this._controls().forEach((el) => {
      if (on) {
        // Remember the pre-flight state so a sold-out variant stays disabled.
        el.__kkPrevDisabled = el.disabled;
        el.disabled = true;
      } else {
        el.disabled = this._available === false ? true : !!el.__kkPrevDisabled;
      }
      el.classList.toggle('is-loading', on);
      el.setAttribute('aria-busy', on ? 'true' : 'false');
    });
    if (this.spinner) this.spinner.hidden = !on;
  }

  /* Contract rule 2: display err.userMessage (already localized German) and NEVER
     err.message / raw exception text. Fall back to a locale string the page
     renders, then to the German constant above. */
  _userMessage(err) {
    if (err && typeof err.userMessage === 'string' && err.userMessage) return err.userMessage;
    const el = document.querySelector('[data-cart-error-text], [data-sticky-error]');
    const fromDom = el && el.getAttribute('data-error-text');
    if (fromDom) return fromDom;
    const i18n = window.KK && window.KK.i18n;
    if (i18n && i18n.cartAddError) return i18n.cartAddError;
    return KK_ADD_ERROR_FALLBACK;
  }

  showError(msg, submitter) {
    const text = msg || KK_ADD_ERROR_FALLBACK;
    if (this.errorBox) {
      if (this.errorText) this.errorText.textContent = text;
      this.errorBox.hidden = false;
    }
    // The buy-box region is off-screen whenever the sticky bar is the control in
    // use (it only appears after the buy box scrolls out the top), so mirror the
    // message into the sticky bar's own live region.
    if (submitter && submitter.hasAttribute && submitter.hasAttribute('data-sticky-atc')) {
      const stickyErr = document.querySelector('[data-sticky-error]');
      if (stickyErr) {
        stickyErr.textContent = text;
        stickyErr.hidden = false;
      } else if (this.errorBox && this.errorBox.scrollIntoView) {
        // No sticky region rendered (older markup): at least bring the message
        // into view rather than leaving the buyer with silence.
        this.errorBox.scrollIntoView({ block: 'center' });
      }
    }
  }

  hideError() {
    if (this.errorBox) this.errorBox.hidden = true;
    const stickyErr = document.querySelector('[data-sticky-error]');
    if (stickyErr) stickyErr.hidden = true;
  }
}

if (!customElements.get('buy-buttons')) customElements.define('buy-buttons', BuyButtons);
